import { ReactNode, LinkHTMLAttributes } from 'react';
import NextLink from 'next/link';
import cn from 'classnames';

const stripTrailingSlash = (href: string) => {
  if (href === `/`) return href;

  return href.endsWith(`/`) ? href.slice(0, -1) : href;
};

export type LinkProps = LinkHTMLAttributes<HTMLAnchorElement> & {
  children: ReactNode;
  target?: string;
  withArrow?: boolean;
};

function ArrowSVG() {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className="duration-100 group-hover:translate-x-1 inline-flex ml-2 transform transition-transform"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.4714 3.52876C10.2111 3.26841 9.78898 3.26841 9.52865 3.52876C9.26831 3.78911 9.26831 4.21122 9.52865 4.47157L12.3906 7.3335H2.00004C1.63185 7.3335 1.33337 7.63196 1.33337 8.00016C1.33337 8.36836 1.63185 8.66683 2.00004 8.66683H12.3906L9.52865 11.5288C9.26831 11.7891 9.26831 12.2112 9.52865 12.4716C9.78898 12.7319 10.2111 12.7319 10.4714 12.4716L14.469 8.47402C14.4772 8.46593 14.4851 8.45763 14.4929 8.44913C14.5429 8.39432 14.5821 8.33317 14.6105 8.26843C14.6467 8.18635 14.6667 8.0956 14.6667 8.00016C14.6667 7.90473 14.6467 7.81398 14.6105 7.73189C14.5821 7.66715 14.5429 7.60601 14.4929 7.55119C14.4851 7.54269 14.4772 7.53439 14.469 7.52631L10.4714 3.52876Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function Link({
  href,
  children,
  className,
  withArrow = false,
  ...props
}: LinkProps) {
  if (!href) return null;

  const anchorLink = href.startsWith(`#`);

  const linkClassName = cn(className, withArrow && `group`);

  if (href.includes(`http`) || href.includes(`mailto`) || anchorLink) {
    return (
      <a
        className={linkClassName}
        href={stripTrailingSlash(href)}
        {...(!anchorLink && { rel: `noopener noreferrer`, target: `_blank` })}
        {...props}
      >
        {children}
        {withArrow && <ArrowSVG />}
      </a>
    );
  }

  return (
    <NextLink href={stripTrailingSlash(href)} prefetch={false}>
      <a className={linkClassName} {...props}>
        {children}
        {withArrow && <ArrowSVG />}
      </a>
    </NextLink>
  );
}
